import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Settings } from '@b4b/shared';
import { api } from '../api/client.js';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function isIosSafariWithoutPwa(): boolean {
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true);
  return isIos && !standalone;
}

async function subscribeDevice(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  const registration = await navigator.serviceWorker.ready;
  const { key } = await api.get<{ key: string }>('/api/push/vapid-public-key');
  if (!key) return false;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
  });
  const json = subscription.toJSON();
  await api.post('/api/push/subscribe', {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  });
  return true;
}

async function unsubscribeDevice(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await api.delete('/api/push/subscribe', { endpoint: subscription.endpoint });
    await subscription.unsubscribe();
  }
}

export default function NotificationSettings({
  settings,
  onPatch,
}: {
  settings: Settings;
  onPatch: (patch: Partial<Settings>) => void;
}) {
  const { t } = useTranslation();
  const [denied, setDenied] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'denied',
  );
  const quiet = settings.quietHours;

  const toggle = async (enabled: boolean) => {
    if (enabled) {
      try {
        const ok = await subscribeDevice();
        if (!ok) {
          setDenied(typeof Notification !== 'undefined' && Notification.permission === 'denied');
          return;
        }
      } catch {
        return;
      }
    } else {
      await unsubscribeDevice().catch(() => undefined);
    }
    onPatch({ notificationsEnabled: enabled });
  };

  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <h2 className="mb-1 font-semibold">{t('settings.notifications')}</h2>
      <p className="mb-3 text-xs text-neutral-500">{t('settings.notificationsHint')}</p>

      <div className="flex items-center justify-between">
        <span className="text-sm">{t('settings.notificationsEnabled')}</span>
        <button
          role="switch"
          aria-checked={settings.notificationsEnabled}
          onClick={() => void toggle(!settings.notificationsEnabled)}
          className={`h-7 w-12 rounded-full p-1 transition ${
            settings.notificationsEnabled ? 'bg-accent' : 'bg-neutral-300 dark:bg-neutral-700'
          }`}
        >
          <span
            className={`block h-5 w-5 rounded-full bg-white transition ${
              settings.notificationsEnabled ? 'translate-x-5' : ''
            }`}
          />
        </button>
      </div>
      {denied && <p className="mt-2 text-xs text-red-500">{t('settings.notificationsDenied')}</p>}
      {isIosSafariWithoutPwa() && (
        <p className="mt-2 text-xs text-neutral-500">{t('settings.iosHint')}</p>
      )}

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm">{t('settings.quietHours')}</span>
        <button
          role="switch"
          aria-checked={quiet !== null}
          onClick={() =>
            onPatch({ quietHours: quiet ? null : { from: '22:00', to: '07:00' } })
          }
          className={`h-7 w-12 rounded-full p-1 transition ${
            quiet ? 'bg-accent' : 'bg-neutral-300 dark:bg-neutral-700'
          }`}
        >
          <span
            className={`block h-5 w-5 rounded-full bg-white transition ${quiet ? 'translate-x-5' : ''}`}
          />
        </button>
      </div>
      {quiet && (
        <div className="mt-2 flex items-center gap-2 text-sm">
          <label>
            {t('settings.quietFrom')}{' '}
            <input
              type="time"
              value={quiet.from}
              onChange={(e) => onPatch({ quietHours: { ...quiet, from: e.target.value } })}
              className="rounded-lg border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>
          <label>
            {t('settings.quietTo')}{' '}
            <input
              type="time"
              value={quiet.to}
              onChange={(e) => onPatch({ quietHours: { ...quiet, to: e.target.value } })}
              className="rounded-lg border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm">{t('settings.timezone')}</span>
        <input
          value={settings.timezone}
          onChange={(e) => onPatch({ timezone: e.target.value })}
          list="timezones"
          className="w-48 rounded-lg border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-950"
        />
        <datalist id="timezones">
          <option value="Europe/Prague" />
          <option value="Europe/London" />
          <option value="America/New_York" />
          <option value="UTC" />
        </datalist>
      </div>
    </section>
  );
}
